import React from 'react';
import { AbsoluteFill, useVideoConfig } from 'remotion';
import { TransitionSeries, linearTiming } from '@remotion/transitions';
import { ProgressBar } from './ProgressBar';
import { fade } from '@remotion/transitions/fade';
import { slide } from '@remotion/transitions/slide';
import { wipe } from '@remotion/transitions/wipe';
import { flip } from '@remotion/transitions/flip';
import { clockWipe } from '@remotion/transitions/clock-wipe';
import { iris } from '@remotion/transitions/iris';
import { none } from '@remotion/transitions/none';
import { ThemeProvider } from './calculate-metadata/theme';
import type { SlideshowProps } from './slideshow-props';

const getEmotionColor = (emotion: string) => {
  switch (emotion) {
    case 'happy':
      return 'lightgreen';
    case 'sad':
      return 'lightblue';
    default:
      return 'lightyellow';
  }
};

const SlideBlock = ({ block }: {
  block: string;
}) => {
  if (block.startsWith('# ')) {
    return <h1 style={{ fontSize: '3em', fontFamily: 'Arial, sans-serif' }}>{block.replace('# ', '')}</h1>;
  }
  if (block.startsWith('- ')) {
    return (
      <ul style={{ fontSize: '2em', fontFamily: 'Arial, sans-serif' }}>
        {block.split('\n').map((item, idx) => (
          <li key={idx}>{item.replace('- ', '')}</li>
        ))}
      </ul>
    );
  }
  return null;
};

type SlideType = { content: string; frontmatter: { [key: string]: any }; title?: string };

const Slide = ({ slide, durationInFrames, fps }:{
  slide: SlideType;
  durationInFrames: number;
  fps: number;
}) => {
  const slideContent = slide.content.split('\n\n');

  return (
    <AbsoluteFill style={{ backgroundColor: getEmotionColor(slide.frontmatter.emotion), justifyContent: 'center', alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
      {slideContent.map((block, idx) => (
        <SlideBlock key={idx} block={block} />
      ))}
    </AbsoluteFill>
  );
};

const getTransition = (type: string, width: number, height: number) => {
  switch (type) {
    case 'fade':
      return fade();
    case 'slide':
      return slide();
    case 'wipe':
      return wipe();
    case 'flip':
      return flip();
    case 'clockWipe':
      return clockWipe({ width, height });
    case 'iris':
      return iris({ width, height });
    case 'none':
      return none();
    default:
      return fade();
  }
};

export const Slideshow = (props: SlideshowProps) => {
  const { slides, themeColors } = props as Required<SlideshowProps>;
  const { width, height, fps } = useVideoConfig();

  if (!Array.isArray(slides) || slides.length === 0) {
    throw new Error('No slides provided to Slideshow — calculateMetadata should supply slides in props.');
  }

  const outerStyle: React.CSSProperties = {
    backgroundColor: themeColors.background,
  };
  const innerStyle: React.CSSProperties = {
    // padding: `${verticalPadding}px 0px`,
  };

  return (
    <ThemeProvider themeColors={themeColors}>
      <AbsoluteFill style={outerStyle}>
        <AbsoluteFill style={{ width: '100%', margin: 'auto' }}>
          <ProgressBar slides={slides} />
          <AbsoluteFill style={innerStyle}>
            <TransitionSeries>
              {slides.map((slide, index) => {
                const durationInFrames = slide.durationInFrames;
                const transitionType = slide.frontmatter.transition || 'fade';
                const transitionDuration = slide.transitionDurationInFrames ?? 0;

                return (
                  <React.Fragment key={index}>
                    <TransitionSeries.Sequence durationInFrames={durationInFrames}>
                      <Slide slide={slide} durationInFrames={durationInFrames} fps={fps} />
                    </TransitionSeries.Sequence>
                    {index < slides.length - 1 && transitionDuration > 0 && (
                      <TransitionSeries.Transition
                        presentation={getTransition(transitionType, width, height) as any}
                        timing={linearTiming({ durationInFrames: transitionDuration })}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </TransitionSeries>
          </AbsoluteFill>
        </AbsoluteFill>
      </AbsoluteFill>
    </ThemeProvider>
  );
};
